<template>
  <ConfirmModal
    v-if="show"
    persist
    wrapper-class="min-w-[600px]"
    :approve="t('Meetings.requests.declineDialog.confirm')"
    :cancel="t('Meetings.requests.declineDialog.cancel')"
    data-testid="meetings-decline-dialog"
    @close-dialog="handleClose"
  >
    <template #title>
      <h1 class="p-6 pb-0 text-headline-m">{{ t('Meetings.requests.declineDialog.title') }}</h1>
    </template>
    <template #content>
      <div class="space-y-4 px-6 pt-4">
        <div>
          <label class="mb-1 block txt-body-s-medium text-subtle">
            {{ t('Meetings.requests.declineDialog.reasonLabel') }}
          </label>
          <SingleSelect
            v-model="selectedReason"
            :options="reasonOptions"
            :label="t('Meetings.requests.declineDialog.selectReason')"
            class="w-full"
            data-testid="meetings-decline-reason-select"
          />
        </div>
        <TextInput
          v-model="customReason"
          type="textarea"
          :max-length="200"
          :label="t('Meetings.requests.declineDialog.customReasonLabel')"
          :placeholder="t('Meetings.requests.declineDialog.customReasonPlaceholder')"
          data-testid="meetings-decline-custom-reason"
        />
      </div>
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
import { TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ConfirmModal from '@/components/ConfirmModal.vue'
import SingleSelect from '@/ui/SingleSelect.vue'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  (e: 'confirm', reason: string): void
  (e: 'cancel'): void
}>()

const { t } = useI18n()

const selectedReason = ref('')
const customReason = ref('')

const reasonOptions = computed(() => [
  { value: 'fully_booked', text: t('Meetings.requests.declineDialog.fullyBooked') },
  { value: 'too_many_requests', text: t('Meetings.requests.declineDialog.tooManyRequests') },
  { value: 'not_our_expertise', text: t('Meetings.requests.declineDialog.notOurExpertise') },
  { value: 'request_too_vague', text: t('Meetings.requests.declineDialog.requestTooVague') },
  { value: 'profile_lacks_info', text: t('Meetings.requests.declineDialog.profileLacksInfo') },
  { value: 'wrong_contact', text: t('Meetings.requests.declineDialog.wrongContact') },
  { value: 'scheduling_conflict', text: t('Meetings.requests.declineDialog.schedulingConflict') },
  { value: 'other', text: t('Meetings.requests.declineDialog.other') },
])

const finalReason = computed(() => {
  if (customReason.value.trim()) {
    return customReason.value.trim()
  }
  if (selectedReason.value && selectedReason.value !== 'other') {
    const option = reasonOptions.value.find((opt) => opt.value === selectedReason.value)
    return option?.text ?? ''
  }
  return ''
})

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      selectedReason.value = ''
      customReason.value = ''
    }
  },
)

function handleClose(confirmed: boolean) {
  if (confirmed) {
    emit('confirm', finalReason.value)
  } else {
    emit('cancel')
  }
}
</script>
